import React, { FC, FormEvent } from 'react';
import { Alert, Grid, Typography } from '@mui/material';
import { Control, UseFormSetValue, FieldErrors, UseFormWatch } from 'react-hook-form';
import { TextField } from '@/components/forms/inputs/TextField/TextField';
import { SelectField } from '@/components/forms/inputs/SelectField/SelectField';
import { InputFieldSuggestions } from '@/components/forms/inputs/InputFieldSuggestions/InputFieldSuggestions';
import { accountFormModel } from '@/components/forms/forms/account/accountFormModel';
import { AccountTabs } from '@/types/enums/forms/account/accountTabs';
import { usePhrase } from '@/hooks/general/usePhrase/usePhrase';
import {
    IAllAccountData,
    IAccountPersonalDetails,
} from '@/types/types/forms/account/account.interface';
import { ACCOUNT_PERSONAL_DETAILS_PHRASES } from '@/consts/phrases/accountPhrases';
import { FOOTER_PHRASES } from '@/consts/phrases/footerPhrases';
import { usePersonalDetails } from '@/hooks/forms/register/usePersonalDetails';
import { DefaultPageProps } from '@/types/types/page/pages';
import { useZipLocations } from '@/hooks/forms/useZipLocations';

interface AccountPersonalDetailsFormInputsProps {
    control: Control<Partial<IAccountPersonalDetails>>;
    dictionary: DefaultPageProps['dictionary'];
    accountData?: IAllAccountData;
    isReadOnly?: boolean;
    errors: FieldErrors<Partial<IAccountPersonalDetails>>;
    setValue: UseFormSetValue<Partial<IAccountPersonalDetails>>;
    watch: UseFormWatch<Partial<IAccountPersonalDetails>>;
}

export const AccountPersonalDetailsFormInputs: FC<AccountPersonalDetailsFormInputsProps> = ({
    control,
    dictionary,
    accountData,
    isReadOnly,
    errors,
    setValue,
    watch,
}) => {
    const { t } = usePhrase();
    const supportUrl = t(FOOTER_PHRASES.SUPPORT_URL, { asString: true }) || '/support';

    const { countriesList, statesList, timezonesList, preferredLangList, checkPreferred } =
        usePersonalDetails({
            dictionary,
            accountData,
        });

    const [zipValue, countryCodeValue] = watch(['zip', 'countryCode']) as [string, string];

    const {
        statesList: zipBasedStatesList,
        citiesList: zipBasedCitiesList,
        isMoreThanOneState: zipBasedIsMoreThanOneState,
        onZipChange,
    } = useZipLocations<Partial<IAccountPersonalDetails>>({
        zipValue,
        countryCodeValue,
        setValue,
    });

    const handleCountryChange = async (val: string | FormEvent<HTMLDivElement>) => {
        try {
            await checkPreferred(val);
            setValue('stateCode', '');
            setValue('preferredLanguageId', null);
        } catch (err) {
            console.error(err);
        }
    };

    const handleZipChange = (val: string) => {
        try {
            onZipChange();
            setValue('zip', val);
        } catch (err) {
            console.error(err);
        }
    };

    const {
        firstName,
        lastName,
        countryCode,
        stateCode,
        zip,
        city,
        address1,
        address2,
        timezoneId,
        preferredLanguageId,
    } = accountFormModel[AccountTabs.PersonalDetails];

    const statesListExist = !!statesList?.length;

    return (
        <>
            <Grid container spacing={2}>
                {accountData?.nameAddressDisabled && (
                    <Grid size={12}>
                        <Typography variant="subtitle2">
                            {t(ACCOUNT_PERSONAL_DETAILS_PHRASES.NOTIFICATION_MESSAGE, {
                                replaceTextVars: [
                                    `<a href="${supportUrl}">${
                                        t(
                                            ACCOUNT_PERSONAL_DETAILS_PHRASES.NOTIFICATION_MESSAGE_SUPPORT_VAR,
                                        ) as string
                                    }</a>`,
                                ],
                            })}
                        </Typography>
                    </Grid>
                )}
                {!accountData?.nameAddressDisabled && accountData?.countryCode === 'USA' && (
                    <Grid size={12} sx={{ mb: 2 }}>
                        <Alert severity="warning" id="warning_box">
                            {t(ACCOUNT_PERSONAL_DETAILS_PHRASES.USA_RESTRICTION_WARNING)}
                        </Alert>
                    </Grid>
                )}
                <Grid size={12}></Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        control={control}
                        name={firstName.name}
                        label={firstName.label}
                        type="text"
                        required
                        fullWidth
                        error={errors.firstName}
                        data-testid="firstname-input"
                        disabled={accountData?.nameAddressDisabled || isReadOnly}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        control={control}
                        name={lastName.name}
                        label={lastName.label}
                        type="text"
                        required
                        fullWidth
                        error={errors.lastName}
                        data-testid="lastname-input"
                        disabled={accountData?.nameAddressDisabled || isReadOnly}
                    />
                </Grid>
                <Grid size={12}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {t(ACCOUNT_PERSONAL_DETAILS_PHRASES.ADDRESS_HEADER)}
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <SelectField
                        control={control}
                        name={countryCode.name}
                        label={countryCode.label}
                        data={countriesList}
                        required
                        fullWidth
                        onChange={(val: string | FormEvent<HTMLDivElement>) =>
                            handleCountryChange(val)
                        }
                        error={errors.countryCode}
                        data-testid="countryCode-input"
                        disabled={accountData?.nameAddressDisabled || isReadOnly}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                        control={control}
                        name={zip.name}
                        label={zip.label}
                        type="text"
                        required
                        fullWidth
                        error={errors.zip}
                        data-testid="zip-input"
                        onChange={(e) => handleZipChange(e.target.value)}
                        disabled={accountData?.nameAddressDisabled || isReadOnly}
                    />
                </Grid>
                {statesListExist && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SelectField
                            control={control}
                            name={stateCode.name}
                            label={stateCode.label}
                            data={statesList ?? []}
                            required
                            fullWidth
                            error={errors.stateCode}
                            data-testid="statecode-input"
                            disabled={
                                !countryCodeValue || accountData?.nameAddressDisabled || isReadOnly
                            }
                        />
                        {zipBasedIsMoreThanOneState && !accountData?.nameAddressDisabled && (
                            <InputFieldSuggestions
                                name="stateCode"
                                data={zipBasedStatesList}
                                setValue={setValue}
                            />
                        )}
                    </Grid>
                )}
                <Grid size={{ xs: 12, sm: statesListExist ? 6 : 12 }}>
                    <TextField
                        control={control}
                        name={city.name}
                        label={city.label}
                        type="text"
                        fullWidth
                        error={errors.city}
                        data-testid="city-input"
                        disabled={accountData?.nameAddressDisabled || isReadOnly}
                    />
                    {!accountData?.nameAddressDisabled && (
                        <InputFieldSuggestions
                            name="city"
                            data={zipBasedCitiesList}
                            setValue={setValue}
                        />
                    )}
                </Grid>
                <Grid size={12}>
                    <TextField
                        control={control}
                        name={address1.name}
                        label={address1.label}
                        type="text"
                        required
                        fullWidth
                        error={errors.address1}
                        data-testid="address1-input"
                        disabled={accountData?.nameAddressDisabled || isReadOnly}
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        control={control}
                        name={address2.name}
                        label={address2.label}
                        type="text"
                        fullWidth
                        error={errors.address2}
                        data-testid="address2-input"
                        disabled={accountData?.nameAddressDisabled || isReadOnly}
                    />
                </Grid>
                {!isReadOnly && (
                    <Grid size={12}>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            {t(ACCOUNT_PERSONAL_DETAILS_PHRASES.SETTINGS_HEADER)}
                        </Typography>
                    </Grid>
                )}
                {!isReadOnly && preferredLangList && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SelectField
                            control={control}
                            name={preferredLanguageId.name}
                            label={preferredLanguageId.label}
                            data={preferredLangList}
                            required
                            fullWidth
                            error={errors.preferredLanguageId}
                            data-testid="preferredLanguageId-input"
                        />
                    </Grid>
                )}
                {!isReadOnly && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <SelectField
                            control={control}
                            name={timezoneId.name}
                            label={timezoneId.label}
                            data={timezonesList}
                            fullWidth
                            error={errors.timezoneId}
                            data-testid="timezoneId-input"
                        />
                    </Grid>
                )}
            </Grid>
        </>
    );
};
